'use client';

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSession } from '@/lib/auth-client';
import { getAdminStats } from '@/lib/actions/auth';
import { getTestimonials } from '@/lib/actions/testimonials';
import {
  Users,
  Briefcase,
  FileText,
  Target,
  Mail,
  Quote,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

export default function AdminDashboardPage() {
  const { data: session, status } = useSession();
  const navigate = useNavigate();
  const [stats, setStats] = useState<
    { label: string; value: number; icon: typeof Mail; color: string }[]
  >([]);

  useEffect(() => {
    if (status === 'loading') return;
    if (!session) {
      navigate('/admin/login', { replace: true });
    }
  }, [session, status, navigate]);

  useEffect(() => {
    if (!session) return;

    Promise.all([getAdminStats(), getTestimonials()]).then(([adminStats, testimonials]) => {
      setStats([
        { label: 'Unread Messages', value: adminStats.messages ?? 0, icon: Mail, color: 'text-red-600' },
        { label: 'Total Insights', value: adminStats.insights ?? 0, icon: FileText, color: 'text-purple-600' },
        { label: 'Testimonials', value: testimonials.length, icon: Quote, color: 'text-primary' },
        { label: 'Team Members', value: adminStats.people ?? 0, icon: Users, color: 'text-blue-600' },
        { label: 'Total Services', value: adminStats.services ?? 0, icon: Target, color: 'text-green-600' },
        { label: 'Open Positions', value: adminStats.careers ?? 0, icon: Briefcase, color: 'text-orange-600' },
      ]);
    });
  }, [session]);

  if (status === 'loading' || !session) {
    return null;
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-navy">Welcome, {session.user?.name}</h1>
        <p className="text-muted-foreground mt-2">Manage your website content efficiently.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {stats.map((stat) => (
          <Card key={stat.label} className="border-0 shadow-sm overflow-hidden group hover:shadow-md transition-shadow">
            <div className={`h-1 w-full bg-current ${stat.color.replace('text-', 'bg-')}`} />
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
                {stat.label}
              </CardTitle>
              <div className={`p-2 rounded-lg bg-muted/50 ${stat.color} group-hover:scale-110 transition-transform`}>
                <stat.icon size={20} />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-navy">{stat.value}</div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
